// the temperature in the clock corner (Jared, Oct 28): every header that carries ws4kp's date and time also gets the
// current temperature, so it's a glance away during Views, Markets or News. It comes from ws4kp's own current
// conditions (the pinned station), the same number as the crawl's "Now", read every minute from its cache: no new
// requests. An observation older than maxAgeMinutes hides it rather than show a stale number without an "as of".
//
// Styles (`?temp=` on the URL to compare, else brand.json headerTemp.style; CSS in _header-temp.scss):
//   gauge   the number, tall, beside the date and time (the default; wide screens)
//   tag     the number on a small plate in the livery, beside the date and time (wide screens)
//   column  a third column: the number on the time's line, the sky in a word on the date's line (wide screens)
//   swap    no new space: the date line trades places with "NOW 57°F" every few seconds (any screen)
//   off     none
import getCurrentWeather from '../currentweather.mjs';

const { brand } = window.RHL;
const cfg = {
	style: 'gauge', maxAgeMinutes: 180, swapSeconds: 6, ...brand.headerTemp,
};
const STYLES = ['gauge', 'tag', 'column', 'swap', 'off'];
const asked = new URLSearchParams(window.location.search).get('temp');
const style = STYLES.includes(asked) ? asked : cfg.style;

let reading = null; // { temp, unit, sky } or null

// the sky in one short word or two, for the column style: the first clause of NWS's text, trimmed to fit
const skyWord = (text) => {
	if (!text) return '';
	const first = String(text).split(/ and |,/)[0].trim();
	return first.length <= 11 ? first : first.split(' ').slice(-1)[0].slice(0, 11);
};

const html = () => {
	if (!reading) return '';
	const { temp, unit, sky } = reading;
	if (style === 'swap') return `NOW ${temp}&deg;${unit}`;
	if (style === 'column') return `<span class="t">${temp}&deg;</span><span class="s">${sky.toUpperCase()}</span>`;
	return `${temp}&deg;`;
};

let last = null;
const draw = () => {
	const now = html();
	document.querySelectorAll('.weather-display .header').forEach((header) => {
		const time = header.querySelector('.date-time.time');
		if (!time) return;
		let el = header.querySelector('.rhl-temp');
		if (!el) {
			el = document.createElement('div');
			el.className = `rhl-temp ${style}`;
			time.after(el);
			header.classList.add(`rhl-temp-${style}`);
		}
		if (el.dataset.html !== now) {
			el.innerHTML = now;
			el.dataset.html = now;
		}
		header.classList.toggle('rhl-temp-on', !!now);
	});
	last = now;
};

const read = async () => {
	try {
		const { data } = await getCurrentWeather();
		const at = data?.features?.[0]?.properties?.timestamp;
		const fresh = at && Date.now() - new Date(at).getTime() < cfg.maxAgeMinutes * 60000;
		if (!fresh || !Number.isFinite(data?.Temperature)) {
			reading = null;
		} else {
			reading = { temp: data.Temperature, unit: data.TemperatureUnit ?? 'F', sky: skyWord(data.TextConditions) };
		}
	} catch {
		reading = null;
	}
	draw();
};

if (style !== 'off') {
	const start = () => {
		read();
		setInterval(read, 60000);
		// headers drawn later (a display's first draw) pick it up within a second
		setInterval(() => {
			if (last !== null) draw();
		}, 1000);
		// the swap's beat; also the 4:3 fallback for the other styles, which have no room beside the clock there
		setInterval(() => document.documentElement.classList.toggle('rhl-temp-swapped'), cfg.swapSeconds * 1000);
	};
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
	else start();
}

// for the control room and the console
window.RHL.headerTemp = () => ({ style, reading });
// RHL.headerTempTest(57, 'Fog') draws a made-up reading until the next minute's read (for checking the look)
window.RHL.headerTempTest = (temp, sky = '') => {
	reading = { temp, unit: 'F', sky: skyWord(sky) };
	draw();
};
